import { App, Plugin, Route, View } from "@pimo/pimo-app-builder";
import { TabLayout } from "@pimo/pimo-components";
import {
  LockDetail,
  type LockDetails,
  ResourceLockPlugin,
} from "@pimo/resource-lock-plugin";
import {
  Attachment,
  BffOe,
  BffOeInitiatives,
  Initiative,
  InitiativeFilterData,
  InitiativeSortData,
  UnsavedAttachment,
  User,
} from "aint-types";
import { LOCK_RESOURCE_NAMES } from "aint-utils";

import { INFormConfirmationDialogType } from "../../components/in-form-confirmation-dialog.tsx";
import { buildFormRoute } from "../../form/build-form-route.tsx";
import { buildReportingFormRoute } from "../../form/build-reporting-form-route.tsx";
import { InAppState } from "../app.tsx";
import { APP_ROUTES } from "../constants";
import { fetchOE, fetchOEInitiatives } from "../helpers/fetch-helpers.ts";
import { buildInitiativesView } from "./initiative-plugin-views/initiatives-view.tsx";
import { buildOeKpisView } from "./initiative-plugin-views/oe-kpis-view.tsx";

const POLLING_INTERVAL = 30000;
const ROUTES_TO_POLL = [
  APP_ROUTES.oeComments,
  APP_ROUTES.editImpactPlan,
  APP_ROUTES.operatingEntities,
] as const;
const RESOURCES_TO_CHECK_FOR_LOCKING = [
  LOCK_RESOURCE_NAMES.initiative,
  LOCK_RESOURCE_NAMES.oeComments,
  LOCK_RESOURCE_NAMES.oeInitiatives,
  LOCK_RESOURCE_NAMES.oeImpactPlan,
];

export interface OEInitiativesPluginPartialState {
  currentOE?: BffOe & {
    lockDetails?: LockDetails;
    userEmailsWithAccess?: User[];
  };
  initiativesLockDetails: Record<number, LockDetail>;
  currentOEInitiatives: BffOeInitiatives;
  currentEditInitiative?: Initiative;
  showEditFormConfirmationDialog: boolean;
  supportingDocumentsDialogOpen: boolean;
  // We put this here in the state, so it can be independently from the edit initiative,
  // to prevent rerenders which would result in data loss in the forms.
  currentSupportingDocuments: Attachment[];
  currentUnsavedSupportingDocuments: UnsavedAttachment[];
  supportingDocumentIdToDelete?: string;
  editFormLoading: boolean;
  editFormSaving: boolean;
  editFormConfirmationDialogType?: INFormConfirmationDialogType;
  editFormNavigationPath?: string;
  previousEditFormConfirmationDialogType?: INFormConfirmationDialogType;
  initiativeFilterData: InitiativeFilterData;
  initiativeSortData: InitiativeSortData;
  draftInitiativeToPublish?: Initiative;
}

type Viewname = "initiatives" | "oeKpis";

export class OeInitiativesPlugin
  implements
    Plugin<InAppState, OEInitiativesPluginPartialState, "oeId" | "viewname">
{
  route?: Route<"oeId" | "viewname", InAppState>;
  private initiativesView?: View<InAppState, TabLayout>;
  private app?: App<InAppState>;
  private resourceLockPollingInterval?: NodeJS.Timeout;

  onRegister(app: App<InAppState>): void {
    this.app = app;
    const tabs: {
      name: string;
      viewname: Viewname;
      path: `/operating-entities/:oeId/${Viewname}`;
    }[] = [
      {
        name: "Initiatives",
        viewname: "initiatives",
        path: `/operating-entities/:oeId/initiatives`,
      },
    ];

    this.initiativesView = app.createView({
      name: "Initiatives",
      layout: new TabLayout(tabs, 2, 3, []),
    });

    const view = this.initiativesView;

    buildInitiativesView(view, app);
    buildOeKpisView(view, app);

    this.setupLockedResourcePolling(app);

    this.route = app.createRoute<"oeId" | "viewname">({
      path: APP_ROUTES.operatingEntities,
      view: this.initiativesView,
    });

    this.route.on("load", async ({ payload }) => {
      const params = payload?.parameters;

      if (!params?.oeId || !this.app) {
        return;
      }

      const [
        {
          aggregatedImpactValues,
          initiatives = [],
          phasesOverview = {},
          enablerOverview = {},
          updateStatusOverview = {
            updateStatus: {},
            completionStatusByEnabler: {},
            updateProgress: 0,
            numberOfInitiatives: 0,
          },
        },
        currentOE,
      ] = await Promise.all([
        fetchOEInitiatives(params.oeId),
        fetchOE(params.oeId),
      ]);
      const lockDetails: LockDetails = await this.getLockDetails(
        app,
        params.oeId
      );

      const initiativesLockDetails = await this.getLockDetailsForInitiative(
        app,
        initiatives
      );

      if (!currentOE?.id) {
        app.navigate(APP_ROUTES.dashboard);

        return;
      }

      const newTabs = [...tabs];

      if (currentOE.isTest) {
        newTabs.push({
          name: "OE KPIs",
          viewname: "oeKpis",
          path: `/operating-entities/:oeId/oeKpis`,
        });
      }
      this.initiativesView?.setLayout(new TabLayout(newTabs, 2, 3, []));

      this.app.patchAppState({
        currentOEInitiatives: {
          aggregatedImpactValues,
          enablerOverview,
          initiatives,
          phasesOverview,
          updateStatusOverview,
        },
        currentOE: {
          ...currentOE,
          lockDetails,
        },
        initiativesLockDetails,
        isLoading: false,
      });
    });

    buildFormRoute(app, this.route);
    buildReportingFormRoute(app, this.route);
  }

  private setupLockedResourcePolling(app: App<InAppState>) {
    const pollForResourcePolling = () => {
      const currentOE = app.getAppState().currentOE;

      if (!currentOE) {
        return;
      }

      void this.getLockDetails(app, currentOE.id).then((lockDetails) =>
        app.patchAppState({
          currentOE: {
            ...currentOE,
            lockDetails,
          },
        })
      );
    };

    this.app?.on("router:route-loaded", ({ payload }) => {
      if (!payload?.path) {
        return;
      }

      if (
        ROUTES_TO_POLL.includes(payload.path as (typeof ROUTES_TO_POLL)[number])
      ) {
        if (!this.resourceLockPollingInterval) {
          this.resourceLockPollingInterval = setInterval(
            pollForResourcePolling,
            POLLING_INTERVAL
          );
        }
        return;
      }

      if (this.resourceLockPollingInterval) {
        clearInterval(this.resourceLockPollingInterval);
      }
      this.resourceLockPollingInterval = undefined;
    });
  }

  private async getLockDetailsForInitiative(
    app: App<InAppState>,
    initiatives: Initiative[]
  ) {
    const resourceLockPlugin =
      app.getPluginByName<ResourceLockPlugin<InAppState>>("ResourceLockPlugin");
    const lockDetails: Record<number, LockDetail> = {};

    for (const initiative of initiatives) {
      lockDetails[initiative.id] = (await resourceLockPlugin?.isLocked(
        `${LOCK_RESOURCE_NAMES.initiative}-${initiative.id}`
      )) ?? { isLocked: false };
    }

    return lockDetails;
  }

  private async getLockDetails(app: App<InAppState>, oeId: string) {
    const resourceLockPlugin =
      app.getPluginByName<ResourceLockPlugin<InAppState>>("ResourceLockPlugin");

    const lockDetails: LockDetails = {};

    for (const object of RESOURCES_TO_CHECK_FOR_LOCKING) {
      const lockDetail = await resourceLockPlugin?.isLocked(
        `${object}-${oeId}`
      );

      if (lockDetail) {
        lockDetails[object] = lockDetail;
      }
    }
    return lockDetails;
  }
}
